import { useViewport } from '@xyflow/react';
import { useMemo } from 'react';

import { useEditCanvasStore } from '../store';
import { getEffectiveViewportRect } from '../utils/getEffectiveViewportRect';
import { toNodeRect } from '../utils/layout/nodeLayout';
import { computeVisibleFraction, getBoundsFromRects } from '../utils/viewportVisibility';

/** Node types that don't represent persisted artwork and shouldn't anchor the hint. */
const EXCLUDED_NODE_TYPES = new Set(['canvasMediaUploadPreview', 'expandingBoundingBox', 'imageGeneration']);

/**
 * Returns `true` when the canvas contains artwork but none of it is currently
 * visible in the viewport (the user has panned/zoomed entirely away from their
 * nodes). Returns `false` when the canvas is empty or any artwork is on screen.
 */
export const useIsArtworkOffScreen = (): boolean => {
  const nodes = useEditCanvasStore((state) => state.nodes);
  const viewport = useViewport();

  const contentBounds = useMemo(() => {
    const rects = nodes.filter((node) => !EXCLUDED_NODE_TYPES.has(node.type)).map(toNodeRect);

    if (rects.length === 0) {
      return undefined;
    }

    return getBoundsFromRects(rects);
  }, [nodes]);

  return useMemo(() => {
    if (!contentBounds) {
      return false;
    }

    const viewportRect = getEffectiveViewportRect(viewport);

    if (!viewportRect) {
      return false;
    }

    return computeVisibleFraction(contentBounds, viewportRect) === 0;
  }, [contentBounds, viewport]);
};
